import { z } from 'zod';
import { Action, AssetRef, CtaPayload, FeaturePayload, FaqItem, GalleryItem, GalleryLayout, PageBlock, PageRoute, ProcessItem, RichText, SplitPayload, Uuid, } from './blocks.js';
export const Slug = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const SiteAccess = z.enum(['Public', 'AdminOnly']);
export const SeoProjection = z
    .object({
    title: z.string().min(1),
    description: z.string().min(1),
    socialImage: AssetRef.optional(),
    noindex: z.boolean(),
})
    .strict();
export const SocialLink = z
    .object({
    label: z.string().min(1),
    href: z
        .string()
        .url()
        .refine((value) => new URL(value).protocol === 'https:', 'Expected an HTTPS URL'),
    handle: z.string().min(1),
})
    .strict();
export const SiteSettingsProjection = z
    .object({
    siteName: z.string().min(1),
    canonicalOrigin: z.string().url().optional(),
    photographerName: z.string().min(1).optional(),
    contactEmail: z.string().email(),
    phone: z.string().min(1).optional(),
    locationLabel: z.string().min(1).optional(),
    serviceArea: z.string().min(1).optional(),
    defaultSocialImage: AssetRef.optional(),
    logo: AssetRef.optional(),
    favicon: AssetRef.optional(),
    socialLinks: z.array(SocialLink),
    globalCta: Action,
    footerText: z.string().min(1),
})
    .strict();
export const NavigationItem = z
    .object({ label: z.string().min(1), href: PageRoute, emphasis: z.boolean().optional() })
    .strict();
export const NavigationProjection = z
    .object({ primary: z.array(NavigationItem), footer: z.array(NavigationItem) })
    .strict();
export const PageKind = z.enum([
    'Home',
    'About',
    'PortfolioIndex',
    'PricingPage',
    'JournalIndex',
    'GiftMarketing',
    'ProposalsService',
    'WeddingsService',
]);
const templateRules = {
    Home: {
        route: '/',
        required: { Hero: [1, 1], Pricing: [1, 1], Process: [1, 1], CTA: [1, 1] },
        allowed: ['Hero', 'Pricing', 'Process', 'CTA', 'EditorialText', 'Feature', 'Gallery'],
    },
    About: {
        route: '/about',
        required: { Hero: [1, 1], EditorialText: [1], CTA: [1, 1] },
        allowed: ['Hero', 'EditorialText', 'CTA', 'Feature', 'Gallery', 'Split'],
    },
    PortfolioIndex: {
        route: '/portfolio',
        required: { Hero: [1, 1], Gallery: [1, 1] },
        allowed: ['Hero', 'Gallery', 'EditorialText', 'CTA'],
    },
    PricingPage: {
        route: '/pricing',
        required: { Hero: [1, 1], Pricing: [1, 1], Process: [1, 1], FAQ: [1, 1] },
        allowed: ['Hero', 'Pricing', 'Process', 'FAQ', 'EditorialText', 'Feature', 'CTA'],
    },
    JournalIndex: {
        route: '/journal',
        required: { Hero: [1, 1], JournalGrid: [1, 1] },
        allowed: ['Hero', 'JournalGrid', 'EditorialText', 'CTA'],
    },
    GiftMarketing: {
        route: '/gift',
        required: { Hero: [1, 1], Process: [1, 1], CTA: [1, 1] },
        allowed: ['Hero', 'Process', 'CTA', 'EditorialText', 'Feature', 'Split', 'FAQ'],
    },
    ProposalsService: {
        route: '/proposals',
        required: { Hero: [1, 1], EditorialText: [1], Process: [1, 1], CTA: [1, 1] },
        allowed: [
            'Hero',
            'EditorialText',
            'Process',
            'CTA',
            'Feature',
            'Gallery',
            'Split',
            'Pricing',
            'FAQ',
        ],
    },
    WeddingsService: {
        route: '/weddings',
        required: { Hero: [1, 1], EditorialText: [1], Process: [1, 1], CTA: [1, 1] },
        allowed: ['Hero', 'EditorialText', 'Process', 'CTA', 'Feature', 'Gallery', 'Split', 'FAQ'],
    },
};
export const PageProjection = z
    .object({
    id: Uuid,
    route: PageRoute,
    kind: PageKind,
    siteAccess: SiteAccess.default('Public'),
    seo: SeoProjection,
    blocks: z.array(PageBlock),
})
    .strict()
    .superRefine((page, ctx) => {
    const rule = templateRules[page.kind];
    if (page.route !== rule.route) {
        ctx.addIssue({ code: 'custom', path: ['route'], message: 'Route does not match page kind' });
    }
    const counts = new Map();
    const enabledPositions = new Set();
    for (const [blockIndex, block] of page.blocks.entries()) {
        if (!rule.allowed.includes(block.type)) {
            ctx.addIssue({ code: 'custom', path: ['blocks'], message: `${block.type} is not allowed` });
        }
        if (!block.enabled)
            continue;
        if (enabledPositions.has(block.position)) {
            ctx.addIssue({
                code: 'custom',
                path: ['blocks', blockIndex, 'position'],
                message: 'Enabled block positions must be unique',
            });
        }
        enabledPositions.add(block.position);
        counts.set(block.type, (counts.get(block.type) ?? 0) + 1);
    }
    if (page.kind === 'PortfolioIndex') {
        const keys = new Set();
        page.blocks.forEach((block, blockIndex) => {
            if (!block.enabled || block.type !== 'Gallery')
                return;
            block.payload.items.forEach((item, itemIndex) => {
                for (const field of ['key', 'category', 'destination', 'external']) {
                    if (item[field] === undefined) {
                        ctx.addIssue({
                            code: 'custom',
                            path: ['blocks', blockIndex, 'payload', 'items', itemIndex, field],
                            message: `Portfolio item requires ${field}`,
                        });
                    }
                }
                if (item.key && keys.has(item.key)) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['blocks', blockIndex, 'payload', 'items', itemIndex, 'key'],
                        message: 'Portfolio item keys must be unique',
                    });
                }
                if (item.key)
                    keys.add(item.key);
            });
        });
    }
    for (const [type, [minimum, maximum]] of Object.entries(rule.required)) {
        const count = counts.get(type) ?? 0;
        if (count < minimum || (maximum !== undefined && count > maximum)) {
            ctx.addIssue({ code: 'custom', path: ['blocks'], message: `Invalid ${type} cardinality` });
        }
    }
});
export const PricingProjection = z
    .object({
    catalogKey: z.literal('main'),
    plans: z.array(z
        .object({
        key: z.string().min(1),
        title: z.string().min(1),
        description: RichText,
        priceLabel: z.string().min(1),
        durationLabel: z.string().min(1).optional(),
        durationMinutes: z.number().int().min(1).max(10080).optional(),
        service: z.enum(['Personal', 'Family', 'Proposals', 'Weddings']).optional(),
        placements: z
            .object({
            pricing: z.boolean(),
            home: z.boolean(),
        })
            .strict()
            .optional(),
        cover: AssetRef.optional(),
        features: z.array(z.string().min(1)),
        action: Action,
        visible: z.boolean(),
    })
        .strict()),
    extras: z.array(z
        .object({ key: z.string().min(1), label: z.string().min(1), priceLabel: z.string().min(1) })
        .strict()),
    steps: z.array(ProcessItem),
    faq: z.array(FaqItem),
})
    .strict();
export const ShootRoute = z.string().regex(/^\/portfolio\/[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const ShootSection = z.discriminatedUnion('type', [
    z.object({ type: z.literal('EditorialText'), body: RichText }).strict(),
    z
        .object({
        type: z.literal('Gallery'),
        layout: GalleryLayout,
        items: z.array(GalleryItem).min(1),
    })
        .strict(),
    z.object({ type: z.literal('Feature'), payload: FeaturePayload }).strict(),
]);
export const ShootProjection = z
    .object({
    id: Uuid,
    position: z.number().int().nonnegative().optional(),
    route: ShootRoute,
    slug: Slug,
    title: z.string().min(1),
    subtitle: z.string().min(1).optional(),
    description: RichText.optional(),
    category: Slug.optional(),
    dateLabel: z.string().min(1).optional(),
    cover: AssetRef,
    seo: SeoProjection,
    sections: z.array(ShootSection),
    cta: CtaPayload,
})
    .strict();
export const JournalArticleRoute = z.string().regex(/^\/journal\/[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const JournalSection = z.discriminatedUnion('type', [
    z.object({ type: z.literal('RichText'), body: RichText }).strict(),
    z
        .object({ type: z.literal('Image'), media: AssetRef, caption: z.string().min(1).optional() })
        .strict(),
    z
        .object({
        type: z.literal('Gallery'),
        layout: GalleryLayout,
        items: z.array(GalleryItem).min(1),
    })
        .strict(),
]);
export const JournalProjection = z
    .object({
    id: Uuid,
    route: JournalArticleRoute,
    slug: Slug,
    title: z.string().min(1),
    excerpt: z.string().min(1),
    category: z.string().min(1),
    dateLabel: z.string().min(1),
    publishedAt: z.iso.datetime().optional(),
    modifiedAt: z.iso.datetime().optional(),
    cover: AssetRef,
    seo: SeoProjection,
    sections: z.array(JournalSection),
})
    .strict();
const forbiddenPresentationKeys = new Set(['bodyClass', 'class', 'className', 'cssClass', 'style']);
const semanticStringKeys = new Set([
    'action',
    'alt',
    'answer',
    'attribution',
    'caption',
    'category',
    'description',
    'emphasis',
    'excerpt',
    'heading',
    'href',
    'icon',
    'label',
    'lead',
    'location',
    'meta',
    'name',
    'note',
    'number',
    'price',
    'question',
    'role',
    'slug',
    'statement',
    'subtitle',
    'tag',
    'text',
    'title',
    'value',
    'year',
]);
const semanticBooleanKeys = new Set(['contact', 'external', 'flip', 'noindex', 'wide']);
const semanticNumberKeys = new Set(['height', 'width']);
const StructuredContentValue = z.json().superRefine((value, ctx) => {
    const visit = (entry, path) => {
        if (Array.isArray(entry)) {
            entry.forEach((item, index) => visit(item, [...path, index]));
            return;
        }
        if (!entry || typeof entry !== 'object')
            return;
        for (const [key, nested] of Object.entries(entry)) {
            const nestedPath = [...path, key];
            if (forbiddenPresentationKeys.has(key)) {
                ctx.addIssue({
                    code: 'custom',
                    path: nestedPath,
                    message: 'Presentation-only keys are not content',
                });
            }
            if (semanticStringKeys.has(key) && typeof nested !== 'string') {
                ctx.addIssue({ code: 'custom', path: nestedPath, message: `${key} must be a string` });
            }
            if (semanticBooleanKeys.has(key) && typeof nested !== 'boolean') {
                ctx.addIssue({ code: 'custom', path: nestedPath, message: `${key} must be a boolean` });
            }
            if (semanticNumberKeys.has(key) && typeof nested !== 'number') {
                ctx.addIssue({ code: 'custom', path: nestedPath, message: `${key} must be a number` });
            }
            visit(nested, nestedPath);
        }
    };
    visit(value, []);
});
const StructuredContentObject = z
    .record(z.string(), StructuredContentValue)
    .superRefine((value, ctx) => {
    for (const [key, nested] of Object.entries(value)) {
        if (forbiddenPresentationKeys.has(key)) {
            ctx.addIssue({
                code: 'custom',
                path: [key],
                message: 'Presentation-only keys are not content',
            });
        }
        if (semanticStringKeys.has(key) && typeof nested !== 'string') {
            ctx.addIssue({ code: 'custom', path: [key], message: `${key} must be a string` });
        }
        if (semanticBooleanKeys.has(key) && typeof nested !== 'boolean') {
            ctx.addIssue({ code: 'custom', path: [key], message: `${key} must be a boolean` });
        }
        if (semanticNumberKeys.has(key) && typeof nested !== 'number') {
            ctx.addIssue({ code: 'custom', path: [key], message: `${key} must be a number` });
        }
    }
});
const SeedSeoPayload = z
    .object({
    title: z.string().min(1),
    description: z.string().min(1),
    canonicalPath: z.string().startsWith('/'),
    noindex: z.boolean(),
    socialImage: AssetRef.optional(),
})
    .strict();
export const SeedPagePayload = z.object({ seo: SeedSeoPayload }).strict();
const SeedChromePayload = z
    .object({
    brand: z.string().min(1),
    explore: z.string().min(1),
    elsewhere: z.string().min(1),
    backToTop: z.string().min(1),
    talk: z.string().min(1),
    bottomTalk: z.string().min(1),
    menu: z.string().min(1),
    contact: z.string().min(1),
    footer: z.string().min(1),
    copyright: z.string().min(1),
    drawer: z
        .object({
        close: z.string().min(1),
        heading: z.string().min(1),
        emphasis: z.string().min(1),
        intro: z.string().min(1),
        fields: z.array(z.string().min(1)),
        wedding: z
            .object({
            heading: z.string().min(1),
            emphasis: z.string().min(1),
            intro: z.string().min(1),
            messageLabel: z.string().min(1),
        })
            .strict()
            .optional(),
        proposal: z
            .object({
            heading: z.string().min(1),
            emphasis: z.string().min(1),
            intro: z.string().min(1),
            messageLabel: z.string().min(1),
        })
            .strict()
            .optional(),
        send: z.string().min(1),
        note: z.string().min(1),
    })
        .strict(),
})
    .strict();
const SeedSettingsPayload = SiteSettingsProjection.extend({
    navigation: NavigationProjection,
    chrome: SeedChromePayload,
}).strict();
const HeroSeedPayload = z
    .object({
    copy: StructuredContentObject.optional(),
    slides: z.array(StructuredContentObject).optional(),
    eyebrow: z.string().min(1).optional(),
    title: z.string().min(1).optional(),
    titleStart: z.string().min(1).optional(),
    titleEmphasis: z.string().min(1).optional(),
    emphasis: z.string().min(1).optional(),
    lead: z.string().min(1).optional(),
    label: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    intro: z.string().min(1).optional(),
    categories: z.string().min(1).optional(),
    empty: z.string().min(1).optional(),
    action: z.string().min(1).optional(),
    includes: z.array(z.string().min(1)).optional(),
    extras: z.string().min(1).optional(),
    giftTitle: z.string().min(1).optional(),
    giftBody: z.string().min(1).optional(),
    giftAction: z.string().min(1).optional(),
    hero: StructuredContentObject.optional(),
    certificate: StructuredContentObject.optional(),
    inquiry: StructuredContentObject.optional(),
    feature: StructuredContentObject.optional(),
    closing: StructuredContentObject.optional(),
    meta: z.array(z.string().min(1)).optional(),
    image: z.string().min(1).optional(),
})
    .strict();
const EditorialSeedPayload = z
    .object({
    label: z.string().min(1).optional(),
    title: z.string().min(1).optional(),
    emphasis: z.string().min(1).optional(),
    titleAfter: z.string().min(1).optional(),
    quoteBefore: z.string().min(1).optional(),
    quoteEmphasis: z.string().min(1).optional(),
    quoteAfter: z.string().min(1).optional(),
    images: z.array(StructuredContentObject).optional(),
    body: StructuredContentObject.optional(),
    intro: StructuredContentObject.optional(),
    factsCopy: StructuredContentObject.optional(),
    locations: z.array(StructuredContentObject).optional(),
    facts: z.array(StructuredContentObject).optional(),
    press: z.array(StructuredContentObject).optional(),
    education: z.array(StructuredContentObject).optional(),
    work: StructuredContentObject.optional(),
    image: StructuredContentObject.optional(),
    statement: z.string().min(1).optional(),
    note: z.string().min(1).optional(),
    gallery: z.array(StructuredContentObject).optional(),
    portfolioLink: StructuredContentObject.optional(),
})
    .strict();
const GallerySeedPayload = z
    .object({
    copy: StructuredContentObject.optional(),
    categories: z.array(StructuredContentObject),
    filters: z.array(StructuredContentObject).optional(),
    items: z.array(StructuredContentObject).optional(),
})
    .strict();
const PricingBlockSeedPayload = z
    .object({
    copy: StructuredContentObject.optional(),
    cards: z.array(StructuredContentObject).optional(),
    catalogKey: z.literal('main').optional(),
    packages: z.array(StructuredContentObject).optional(),
    extras: z.array(StructuredContentObject).optional(),
    ownershipVersion: z.literal(1).optional(),
    extrasHeading: z.string().min(1).optional(),
    gift: z
        .object({
        heading: z.string().min(1),
        body: RichText,
        actionLabel: z.string().min(1),
    })
        .strict()
        .optional(),
})
    .strict();
const ProcessSeedPayload = z
    .object({
    copy: StructuredContentObject.optional(),
    steps: z.array(StructuredContentObject).optional(),
    details: z.array(StructuredContentObject).optional(),
    title: z.string().min(1).optional(),
    titleStart: z.string().min(1).optional(),
    titleEmphasis: z.string().min(1).optional(),
    meta: z.array(z.string().min(1)).optional(),
    intro: StructuredContentObject.optional(),
    cta: StructuredContentObject.optional(),
    journey: StructuredContentObject.optional(),
    collections: z.array(StructuredContentObject).optional(),
    reviews: z.array(StructuredContentObject).optional(),
    statement: StructuredContentObject.optional(),
    source: z.enum(['Pricing', 'Inline']).optional(),
    eyebrow: z.string().min(1).optional(),
    heading: z.string().min(1).optional(),
    accent: z.string().min(1).optional(),
    actionLabel: z.string().min(1).optional(),
    items: z.array(ProcessItem).optional(),
})
    .strict();
const FaqBlockSeedPayload = z
    .object({
    copy: StructuredContentObject.optional(),
    source: z.enum(['Pricing', 'Inline']).optional(),
    eyebrow: z.string().min(1).optional(),
    heading: z.string().min(1).optional(),
    accent: z.string().min(1).optional(),
    items: z.union([z.array(StructuredContentObject), z.array(FaqItem)]),
})
    .strict();
const JournalGridSeedPayload = z
    .object({
    filters: z.array(StructuredContentObject),
    featured: StructuredContentObject,
    articles: z.array(StructuredContentObject),
})
    .strict();
const CtaSeedPayload = z
    .object({
    label: z.string().min(1).optional(),
    title: z.string().min(1).optional(),
    emphasis: z.string().min(1).optional(),
    action: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    featuredLabel: z.string().min(1).optional(),
    featuredTitle: z.string().min(1).optional(),
    featuredEmphasis: z.string().min(1).optional(),
    learningLabel: z.string().min(1).optional(),
    learningTitle: z.string().min(1).optional(),
    learningEmphasis: z.string().min(1).optional(),
    learningNote: z.string().min(1).optional(),
    ctaLabel: z.string().min(1).optional(),
    ctaTitle: z.string().min(1).optional(),
    ctaEmphasis: z.string().min(1).optional(),
    ctaBody: z.string().min(1).optional(),
    ctaAction: z.string().min(1).optional(),
})
    .strict();
const seedBlock = (type, payload) => z
    .object({
    key: z.string().min(1),
    type: z.literal(type),
    payload,
})
    .strict();
export const DraftPageBlock = z.discriminatedUnion('type', [
    seedBlock('Hero', HeroSeedPayload),
    seedBlock('EditorialText', EditorialSeedPayload),
    seedBlock('Feature', FeaturePayload.extend({ image: StructuredContentObject.optional() })),
    seedBlock('Gallery', GallerySeedPayload),
    seedBlock('Split', SplitPayload),
    seedBlock('Pricing', PricingBlockSeedPayload),
    seedBlock('Process', ProcessSeedPayload),
    seedBlock('FAQ', FaqBlockSeedPayload),
    seedBlock('JournalGrid', JournalGridSeedPayload),
    seedBlock('CTA', CtaSeedPayload),
]);
export const PricingPlanSeedPayload = z
    .object({
    kind: z.string().min(1),
    title: z.string().min(1),
    description: z.string().min(1),
    includes: z.array(z.string().min(1)),
    price: z.string().min(1),
    pricePrefix: z.string().min(1).optional(),
    image: z.string().min(1),
    alt: z.string().min(1),
    ctaLabel: z.string().min(1),
    service: z.enum(['Personal', 'Family', 'Proposals', 'Weddings']).optional(),
    placements: z
        .object({
        pricing: z.boolean(),
        home: z.boolean(),
    })
        .strict()
        .optional(),
    destination: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('drawer') }).strict(),
        z.object({ kind: z.literal('route'), href: z.string().startsWith('/') }).strict(),
    ]),
    preview: z
        .union([
        z.object({ image: z.string().min(1) }).strict(),
        z.object({ includesLimit: z.number().int().positive() }).strict(),
    ])
        .optional(),
})
    .strict();
export const PricingFeatureSeedPayload = z.object({ text: z.string().min(1) }).strict();
export const PricingExtraSeedPayload = z
    .object({ title: z.string().min(1), price: z.string().min(1) })
    .strict();
export const PricingStepSeedPayload = z
    .object({
    number: z.string().min(1),
    title: z.string().min(1),
    description: z.string().min(1),
    action: z.string().min(1).optional(),
})
    .strict();
export const PricingFaqSeedPayload = z
    .object({ question: z.string().min(1), answer: z.string().min(1) })
    .strict();
const ShootSeedPayload = z
    .object({
    title: z.string().min(1),
    genre: z.string().min(1),
    location: z.string().min(1),
    year: z.string().min(1),
    intro: StructuredContentObject,
    next: StructuredContentObject,
    cta: StructuredContentObject,
    nextStoryLabel: z.string().min(1),
    seo: SeedSeoPayload,
})
    .strict();
const ShootGallerySeedPayload = z
    .object({
    type: z.enum(['gallery', 'duo']),
    images: z.array(StructuredContentObject).min(1),
})
    .strict();
const ShootFeatureSeedPayload = z
    .object({
    type: z.literal('feature'),
    variant: z.string().min(1),
    image: StructuredContentObject,
    label: z.string().min(1).optional(),
    statement: z.string().min(1).optional(),
    flip: z.boolean().optional(),
})
    .strict();
const DraftShootSection = z.discriminatedUnion('type', [
    seedBlock('Gallery', ShootGallerySeedPayload),
    seedBlock('Feature', ShootFeatureSeedPayload),
]);
const JournalSeedPayload = z
    .object({
    slug: Slug,
    title: z.string().min(1),
    excerpt: z.string().min(1),
    category: z.string().min(1),
    publishedLabel: z.string().min(1),
    readTimeLabel: z.string().min(1),
    indexEyebrow: z.string().min(1),
    cover: StructuredContentObject,
    seo: SeedSeoPayload,
    copy: StructuredContentObject,
    related: z.array(StructuredContentObject),
    relatedCopy: StructuredContentObject,
    quote: z.string().min(1),
    planning: StructuredContentObject,
    author: StructuredContentObject,
    cta: StructuredContentObject,
})
    .strict();
const JournalRichTextSeedPayload = z
    .object({ heading: z.string().min(1), body: z.string().min(1) })
    .strict();
const JournalImageSeedPayload = z
    .object({
    src: z.string().min(1),
    alt: z.string().min(1),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    caption: z.string().min(1),
})
    .strict();
const DraftJournalSection = z.discriminatedUnion('type', [
    seedBlock('RichText', JournalRichTextSeedPayload),
    seedBlock('Image', JournalImageSeedPayload),
]);
/** A mutable, pre-release view: intentionally contains no release asset pins. */
export const DraftContentProjection = z
    .object({
    settings: z.object({ key: z.string().min(1), payload: SeedSettingsPayload }).strict(),
    pages: z
        .array(z
        .object({
        key: z.string().min(1),
        route: PageRoute,
        slug: Slug,
        kind: PageKind,
        siteAccess: z.literal('AdminOnly'),
        payload: SeedPagePayload,
        blocks: z.array(DraftPageBlock),
    })
        .strict())
        .length(8),
    pricing: z
        .object({
        key: z.string().min(1),
        catalogKey: z.literal('main'),
        visibility: z.literal('Private'),
        payload: z
            .object({
            catalogKey: z.literal('main'),
            copy: StructuredContentObject,
            homepageCards: z.array(StructuredContentObject),
        })
            .strict(),
        plans: z.array(z
            .object({
            key: z.string().min(1),
            planKey: z.string().min(1),
            payload: PricingPlanSeedPayload,
            features: z.array(z
                .object({
                key: z.string().min(1),
                payload: PricingFeatureSeedPayload,
            })
                .strict()),
        })
            .strict()),
        extras: z.array(z.object({ key: z.string().min(1), payload: PricingExtraSeedPayload }).strict()),
        steps: z.array(z.object({ key: z.string().min(1), payload: PricingStepSeedPayload }).strict()),
        faq: z.array(z.object({ key: z.string().min(1), payload: PricingFaqSeedPayload }).strict()),
    })
        .strict(),
    shoots: z
        .array(z
        .object({
        key: z.string().min(1),
        route: ShootRoute,
        slug: Slug,
        siteAccess: z.literal('AdminOnly'),
        payload: ShootSeedPayload,
        sections: z.array(DraftShootSection),
    })
        .strict())
        .length(2),
    journal: z
        .array(z
        .object({
        key: z.string().min(1),
        route: JournalArticleRoute,
        slug: Slug,
        siteAccess: z.literal('AdminOnly'),
        payload: JournalSeedPayload,
        sections: z.array(DraftJournalSection),
    })
        .strict())
        .length(1),
})
    .strict()
    .superRefine((draft, ctx) => {
    const routes = [...draft.pages, ...draft.shoots, ...draft.journal].map((entry) => entry.route);
    if (new Set(routes).size !== routes.length) {
        ctx.addIssue({ code: 'custom', path: ['pages'], message: 'Draft routes must be unique' });
    }
    for (const [pageIndex, page] of draft.pages.entries()) {
        const rule = templateRules[page.kind];
        if (page.route !== rule.route) {
            ctx.addIssue({
                code: 'custom',
                path: ['pages', pageIndex, 'route'],
                message: 'Route does not match page kind',
            });
        }
        const counts = new Map();
        for (const block of page.blocks) {
            if (!rule.allowed.includes(block.type)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['pages', pageIndex, 'blocks'],
                    message: `${block.type} is not allowed`,
                });
            }
            counts.set(block.type, (counts.get(block.type) ?? 0) + 1);
        }
        for (const [type, [minimum, maximum]] of Object.entries(rule.required)) {
            const count = counts.get(type) ?? 0;
            if (count < minimum || (maximum !== undefined && count > maximum)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['pages', pageIndex, 'blocks'],
                    message: `Invalid ${type} cardinality`,
                });
            }
        }
    }
});
export { templateRules as pageTemplateRules };
